import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Sun, Moon, Loader2, CheckCircle2 } from 'lucide-react';

const ROLES = [
  'RAN Optimization Engineer',
  'Core / 5GC Engineer',
  'Integration / SI Engineer',
  'Managed Services / NOC',
  'Deployment Engineer',
  'Engineering Manager / Lead',
  'CTO / VP Engineering',
  'Other',
];

const DOMAINS = ['RAN', 'Core', 'Integration', 'Managed Services', 'NOC', 'Training', 'Other'];

const TEAM_SIZES = ['1-5', '6-25', '26-100', '100+'];

const WORKFLOWS = [
  'RAN optimization',
  'Core optimization',
  'Network integration',
  'Managed services / NOC',
  'Deployment & commissioning',
  'RCA & troubleshooting',
  'SOP retrieval',
  'Multivendor mapping',
  'Parameter lookup',
  'Training & onboarding',
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Saudi Arabia',
  'Singapore', 'Australia', 'Germany', 'France', 'Brazil', 'Japan', 'South Korea',
  'Indonesia', 'Vietnam', 'Philippines', 'Other',
];

export default function RequestAccessPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    business_email: '',
    name: '',
    company: '',
    role: '',
    telecom_domain: '',
    country: '',
    team_size: '',
    requested_workflows: [],
    use_case: '',
  });

  const update = (k) => (e) =>
    setForm((p) => Object.assign({}, p, { [k]: e?.target ? e.target.value : e }));

  const toggleWorkflow = (w) => {
    setForm((p) => {
      const has = p.requested_workflows.indexOf(w) >= 0;
      const next = has
        ? p.requested_workflows.filter(function (x) { return x !== w; })
        : p.requested_workflows.concat([w]);
      return Object.assign({}, p, { requested_workflows: next });
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Light client-side validation
    const required = ['business_email', 'name', 'company', 'role', 'telecom_domain', 'country', 'team_size', 'use_case'];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === '') {
        toast.error('Please fill in: ' + k.replace('_', ' '));
        return;
      }
    }
    if (form.use_case.trim().length < 20) {
      toast.error('Please share a few sentences about your use case (≥ 20 characters).');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({}, form, { source: 'landing' })),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.detail || 'Submission failed');
      }
      setDone(true);
      toast.success('Request received. Our team will be in touch.');
    } catch (err) {
      toast.error(err.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="request-access-page">
      <nav className="px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="back-to-home">
          <img
            src="/queryverse-logo.png"
            alt="QueryVerse"
            className="h-10"
          />
          <span className="text-[10px] text-muted-foreground hidden sm:block italic">
            a product by AppCenn Pvt Ltd
          </span>
        </Link>
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-full hover:bg-secondary transition-colors"
          data-testid="request-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
      </nav>

      <main className="flex-1 px-6 py-10">
        <div className="max-w-2xl mx-auto">
          {done ? (
            <div
              className="rounded-2xl border border-border bg-card p-10 text-center"
              data-testid="request-access-success"
            >
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Request received</h1>
              <p className="text-muted-foreground mb-6">
                Thanks {form.name.split(' ')[0]}. Our team will review your request
                and reach out to <strong>{form.business_email}</strong> within
                1–2 business days.
              </p>
              <Button onClick={() => navigate('/')} data-testid="back-home-btn" className="rounded-full">
                Back to home
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="request-access-form">
              <header className="mb-2">
                <p className="text-xs uppercase tracking-wider text-primary font-medium">
                  Request access
                </p>
                <h1 className="text-3xl font-semibold mt-2">
                  Tell us about your team.
                </h1>
                <p className="text-muted-foreground mt-3 text-sm">
                  QueryVerse is a curated multivendor telecom operational intelligence
                  platform. Access is granted to qualified engineering teams.
                  We'll review your request and reach out within 1–2 business days.
                </p>
              </header>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Jane Smith"
                    data-testid="ra-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_email">Business email *</Label>
                  <Input
                    id="business_email"
                    type="email"
                    value={form.business_email}
                    onChange={update('business_email')}
                    placeholder="you@company.com"
                    data-testid="ra-email"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Company *</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Acme Telecom"
                    data-testid="ra-company"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select value={form.country} onValueChange={update('country')}>
                    <SelectTrigger data-testid="ra-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(function (c) {
                        return (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Select value={form.role} onValueChange={update('role')}>
                    <SelectTrigger data-testid="ra-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(function (r) {
                        return (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Primary domain *</Label>
                  <Select
                    value={form.telecom_domain}
                    onValueChange={update('telecom_domain')}
                  >
                    <SelectTrigger data-testid="ra-domain">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOMAINS.map(function (d) {
                        return (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Team size *</Label>
                  <Select value={form.team_size} onValueChange={update('team_size')}>
                    <SelectTrigger data-testid="ra-team-size">
                      <SelectValue placeholder="Team size" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAM_SIZES.map(function (s) {
                        return (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Workflows you'd use QueryVerse for (pick any)</Label>
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                  data-testid="ra-workflows"
                >
                  {WORKFLOWS.map(function (w) {
                    const checked = form.requested_workflows.indexOf(w) >= 0;
                    return (
                      <label
                        key={w}
                        className={
                          'flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors text-sm ' +
                          (checked
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-secondary')
                        }
                        data-testid={`ra-workflow-${w.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={function () { toggleWorkflow(w); }}
                        />
                        <span>{w}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="use_case">Briefly describe your use case *</Label>
                <Textarea
                  id="use_case"
                  value={form.use_case}
                  onChange={update('use_case')}
                  placeholder="e.g., 25-engineer integration team operating Samsung + Nokia 5G RAN; need rapid SOP lookup and parameter cross-reference for commissioning."
                  rows={4}
                  data-testid="ra-use-case"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  A couple of sentences is enough — vendors, workflows, what's slowing you down today.
                </p>
              </div>

              <div className="pt-2 flex items-center gap-3">
                <Button
                  type="submit"
                  className="rounded-full px-6 h-11"
                  disabled={loading}
                  data-testid="ra-submit"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Submit request'
                  )}
                </Button>
                <Link
                  to="/auth"
                  className="text-sm text-muted-foreground hover:text-foreground"
                  data-testid="ra-existing-customer"
                >
                  Already a customer? Sign in →
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>

      <footer className="px-6 py-4 text-center">
        <p className="text-xs text-muted-foreground">
          By submitting, you agree to be contacted about QueryVerse access.
        </p>
      </footer>
    </div>
  );
}
